import React, { useEffect, useMemo, useState } from 'react';
import { profitabilityAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  ChevronLeft, ChevronRight, Loader2, Settings2, TrendingUp, TrendingDown,
  AlertTriangle, Clock, Euro,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (v) => (v == null ? '—' : `${v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const fmtHM = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const ProfitabilityTab = () => {
  const [monthRef, setMonthRef] = useState(isoDate(new Date()).slice(0, 7)); // YYYY-MM
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Tarifas
  const [ratesOpen, setRatesOpen] = useState(false);
  const [ratesData, setRatesData] = useState(null);
  const [ratesForm, setRatesForm] = useState({ default_hourly_cost: '', rates: {} });
  const [savingRates, setSavingRates] = useState(false);

  const range = useMemo(() => {
    const [y, m] = monthRef.split('-').map(Number);
    return { from: `${monthRef}-01`, to: isoDate(new Date(y, m, 0)) };
  }, [monthRef]);

  const monthLabel = useMemo(() => {
    const [y, m] = monthRef.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  }, [monthRef]);

  const shiftMonth = (dir) => {
    const [y, m] = monthRef.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonthRef(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await profitabilityAPI.get(range.from, range.to);
      setData(res);
    } catch (e) {
      toast.error(e.message || 'No se pudo cargar la rentabilidad');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRates = async () => {
    setRatesOpen(true);
    try {
      const r = await profitabilityAPI.getRates();
      setRatesData(r);
      const rates = {};
      (r.members || []).forEach((m) => { rates[m.id] = m.hourly_cost != null ? String(m.hourly_cost) : ''; });
      setRatesForm({ default_hourly_cost: String(r.default_hourly_cost ?? 20), rates });
    } catch (e) {
      toast.error(e.message || 'No se pudieron cargar las tarifas');
    }
  };

  const saveRates = async () => {
    if (savingRates) return;
    setSavingRates(true);
    try {
      await profitabilityAPI.saveRates({
        default_hourly_cost: parseFloat(ratesForm.default_hourly_cost) || 0,
        rates: Object.entries(ratesForm.rates).map(([user_id, v]) => ({
          user_id,
          hourly_cost: v === '' ? null : parseFloat(v),
        })),
      });
      toast.success('Tarifas guardadas');
      setRatesOpen(false);
      load();
    } catch (e) {
      toast.error(e.message || 'No se pudieron guardar');
    }
    setSavingRates(false);
  };

  const marginColor = (m) => (m == null ? 'text-muted-foreground' : m >= 0 ? 'text-emerald-600' : 'text-red-600');
  const t = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="profitability-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Euro className="w-5 h-5 text-primary" /> Rentabilidad por cliente</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Coste = horas registradas × tarifa €/h de cada persona · Ingresos = facturas de Quipu del mes</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 border rounded-lg bg-background px-1 py-0.5">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftMonth(-1)} data-testid="prof-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium capitalize min-w-[130px] text-center" data-testid="prof-month-label">{monthLabel}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftMonth(1)} data-testid="prof-next-btn"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <Button variant="outline" size="sm" onClick={openRates} data-testid="prof-rates-btn">
            <Settings2 className="w-4 h-4 mr-1.5" /> Tarifas del equipo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : !data ? null : (
        <>
          {!data.quipu_ok && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="py-3 text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" /> No se pudieron cargar los ingresos de Quipu ahora mismo. Los costes son correctos; los ingresos aparecen vacíos.
              </CardContent>
            </Card>
          )}

          {/* Totales */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card data-testid="prof-total-revenue"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Ingresos del mes</p>
              <p className="text-2xl font-bold text-primary mt-1">{fmtEur(t.revenue)}</p>
            </CardContent></Card>
            <Card data-testid="prof-total-cost"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Coste en horas</p>
              <p className="text-2xl font-bold mt-1">{fmtEur(t.cost)}</p>
            </CardContent></Card>
            <Card data-testid="prof-total-margin"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Margen</p>
              <p className={`text-2xl font-bold mt-1 flex items-center gap-1.5 ${marginColor(t.margin)}`}>
                {t.margin != null && (t.margin >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />)}
                {fmtEur(t.margin)}
              </p>
            </CardContent></Card>
          </div>

          {/* Tabla por cliente */}
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="prof-clients-table">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="px-4 py-3 font-medium">Cliente</th>
                    <th className="px-3 py-3 font-medium text-right">Horas</th>
                    <th className="px-3 py-3 font-medium text-right">Coste</th>
                    <th className="px-3 py-3 font-medium text-right">Ingresos</th>
                    <th className="px-3 py-3 font-medium text-right">Margen</th>
                    <th className="px-3 py-3 font-medium text-right">Margen %</th>
                    <th className="px-3 py-3 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.clients || []).flatMap((c) => {
                    const rows = [
                      <tr
                        key={c.client_id}
                        className="border-b last:border-0 hover:bg-muted/40 cursor-pointer transition-colors"
                        onClick={() => setExpanded(expanded === c.client_id ? null : c.client_id)}
                        data-testid={`prof-row-${c.client_id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{c.client_name}</span>
                            {!c.quipu_linked && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Sin Quipu</Badge>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">{fmtHM(c.minutes)}</td>
                        <td className="px-3 py-3 text-right">{fmtEur(c.cost)}</td>
                        <td className="px-3 py-3 text-right font-medium">{fmtEur(c.revenue)}</td>
                        <td className={`px-3 py-3 text-right font-bold ${marginColor(c.margin)}`} data-testid={`prof-margin-${c.client_id}`}>{fmtEur(c.margin)}</td>
                        <td className={`px-3 py-3 text-right font-medium ${marginColor(c.margin)}`}>{c.margin_pct != null ? `${c.margin_pct}%` : '—'}</td>
                        <td className="px-3 py-3"><ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expanded === c.client_id ? 'rotate-90' : ''}`} /></td>
                      </tr>,
                    ];
                    if (expanded === c.client_id) {
                      rows.push(
                        <tr key={`${c.client_id}-detail`} className="bg-muted/20">
                          <td colSpan={7} className="px-6 py-3">
                            {c.people.length === 0 ? (
                              <p className="text-xs text-muted-foreground">Sin horas registradas en este cliente este mes.</p>
                            ) : (
                              <div className="space-y-1.5">
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Quién le dedicó tiempo</p>
                                {c.people.map((p) => (
                                  <div key={p.user_id} className="flex items-center justify-between text-sm" data-testid={`prof-person-${c.client_id}-${p.user_id}`}>
                                    <span className="text-muted-foreground">{p.name} <span className="text-[11px]">({p.rate} €/h)</span></span>
                                    <span>{fmtHM(p.minutes)} · <strong>{fmtEur(p.cost)}</strong></span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    }
                    return rows;
                  })}
                  {(data.clients || []).length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-muted-foreground text-sm">Sin actividad (horas o facturas) en este mes.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Horas sin repartir */}
          {data.unassigned?.minutes > 0 && (
            <Card className="border-amber-200" data-testid="prof-unassigned-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                  <Clock className="w-4 h-4" /> Horas sin repartir por cliente: {fmtHM(data.unassigned.minutes)} · {fmtEur(data.unassigned.cost)}
                </CardTitle>
                <CardDescription>Jornadas fichadas sin cronómetro de tarea y tareas sin cliente asignado. Este coste no está imputado a ninguna cuenta.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {data.unassigned.people.map((p) => (
                  <div key={p.user_id} className="flex items-center justify-between text-sm" data-testid={`prof-unassigned-${p.user_id}`}>
                    <span className="text-muted-foreground">{p.name}</span>
                    <span>{fmtHM(p.minutes)} · {fmtEur(p.cost)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Dialog Tarifas */}
      <Dialog open={ratesOpen} onOpenChange={setRatesOpen}>
        <DialogContent className="max-w-lg" data-testid="rates-dialog">
          <DialogHeader><DialogTitle>Tarifas del equipo (€/hora)</DialogTitle></DialogHeader>
          {!ratesData ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                💡 <strong>Cómo calcularlo:</strong> coste mensual de esa persona para la empresa (bruto + SS) ÷ horas al mes.
                Jornada completa ≈ 160h · Media jornada ≈ 80h. Ej.: 1.000 €/mes a media jornada → 1.000 ÷ 80 = <strong>12,5 €/h</strong>.
              </p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">Tarifa por defecto</span>
                <div className="flex items-center gap-1.5">
                  <Input type="number" min="0" step="0.5" className="h-9 w-24 text-right" value={ratesForm.default_hourly_cost}
                    onChange={(e) => setRatesForm((f) => ({ ...f, default_hourly_cost: e.target.value }))} data-testid="rate-default-input" />
                  <span className="text-sm text-muted-foreground">€/h</span>
                </div>
              </div>
              <div className="divide-y rounded-lg border">
                {(ratesData.members || []).map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{m.name}</p>
                      <p className="text-[11px] text-muted-foreground">{m.role === 'ceo' ? 'CEO' : 'Equipo'} · {m.time_tracking_method === 'jornada' ? 'jornada' : 'por tarea'}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input type="number" min="0" step="0.5" className="h-9 w-24 text-right" placeholder={ratesForm.default_hourly_cost || '20'}
                        value={ratesForm.rates[m.id] ?? ''}
                        onChange={(e) => setRatesForm((f) => ({ ...f, rates: { ...f.rates, [m.id]: e.target.value } }))}
                        data-testid={`rate-input-${m.id}`} />
                      <span className="text-sm text-muted-foreground">€/h</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Si dejas la casilla vacía se usa la tarifa por defecto. Solo las CEOs veis estas tarifas y los costes.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatesOpen(false)}>Cancelar</Button>
            <Button onClick={saveRates} disabled={savingRates || !ratesData} data-testid="rates-save-btn">
              {savingRates && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfitabilityTab;
